import React, { useState } from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import dateFormat from 'dateformat';
import TimelineIcon from '@mui/icons-material/Timeline';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';
import { Typography } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { isThisYear } from 'date-fns';
import { formatter, getLastCompletedStepIndex } from '#component/shipment/helper';
import AlertStatus from '#component/shipment/dashboard/alert-status';
import PreloaderComponent from '#component/preloader';
import NotFoundMessage from '#component/general/not-found-message';
import SimpleCardTemplate from '#component/general/simple-card-template';
import MobileDrawerMenu from '#component/general/mobile-drawer-menu/mobile-drawer-menu';
import LargeFixedButton from '#component/general/large-fixed-button';
import DeleteDialog from '#component/shipment/overview/delete-dialog';
import { getAddressString } from '#component/shipment/overview/shipment-timeline/timeline-step';
import HighlightWord from '#component/general/highlight-word/highlight-word';
import { getHighlightedSearchResult } from '#component/general/highlight-word/helper';
import StepProgressBar from './step-progress-bar';
import DevicesCountButton from './devices-count-button';
import { CardsCnt, Icon, StepProgressBarCnt } from './styles';
import { getDateTimeFormat } from '#helper';

export const dateFormatter = (date, account) => (Date.parse(date) ?
  dateFormat(
    date,
    getDateTimeFormat(account && account.measurementSystem, isThisYear(new Date(date)) ? 'dateTime' : 'dateShort')
  ) : null);

const CardsView = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    shipments,
    status
  } = useSelector(s => s.shipmentsPaginated, shallowEqual);
  const listFilter = useSelector(s => s.listFilter, shallowEqual);
  const shipmentFilter = listFilter?.shipment;
  const account = useSelector(s => s.account, shallowEqual);
  const [isDrawerMenuOpen, setIsDrawerMenuOpen] = useState(false);
  const [shipmentItem, setShipmentItem] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const loading = status === 'loading';
  const highlightedSearchResult = getHighlightedSearchResult(listFilter);

  if (shipmentFilter && !loading && !shipments.length) {
    return (
      <NotFoundMessage style={{ marginTop: -50 }}>
        No shipments
      </NotFoundMessage>
    );
  }

  const handleDrawerMenuOpen = () => {
    setIsDrawerMenuOpen(true);
  };

  const handleShipmentTimelineClick = () => history.push(`/shipment/${shipmentItem.id}`);
  const handleSettingsClick = () => history.push(`/shipment/edit/${shipmentItem.id}`);
  const handleAudutTrailClick = () => history.push(`shipment-log/?shipment=${shipmentItem.id}`);
  const handleDeleteShipmentClick = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteShipmentSuccess = () => {
    setIsDrawerMenuOpen(false);
  };

  const menuItems = [
    { name: 'Timeline', onClick: handleShipmentTimelineClick, icon: <TimelineIcon /> },
    { name: 'Settings', onClick: handleSettingsClick, icon: <SettingsIcon /> },
    { name: 'Audit trail', onClick: handleAudutTrailClick, icon: <SpeakerNotesIcon /> },
    { name: 'Delete', onClick: handleDeleteShipmentClick, icon: <DeleteIcon /> }
  ];

  return (
    !loading ? (
      <>
        <CardsCnt>
          {shipments.map(shipment => {
            const {
              id,
              uid,
              label,
              startDate,
              endDate,
              delivery = [],
              devices = [],
              project = {},
              logs,
              done,
              status: shipmentStatus
            } = shipment;
            const lastCompletedStepIndex = getLastCompletedStepIndex(delivery);
            const destinationCountry = delivery.length > 0 ?
              getAddressString(delivery[delivery.length - 1].address) : undefined;
            const originCountry = delivery.length > 0 ?
              getAddressString(delivery[0]?.address) : undefined;

            return (
              <SimpleCardTemplate
                key={id}
                onCardClickHeader={() => history.push(`/shipment/edit/${id}`)}
                title={
                  <Typography
                    color="textSecondary"
                    fontWeight={500}
                    m={0}
                    textAlign="left"
                  >
                    {highlightedSearchResult ? <HighlightWord
                      textToHighlight={label || formatter(uid)}
                      searchWord={highlightedSearchResult}
                    /> : label || formatter(uid)}
                  </Typography>
                }
                icon={
                  <Icon>
                    <AlertStatus logs={logs} isMobile={true} />
                  </Icon>
                }
                cardContents={[
                  {
                    title: 'Devices',
                    content: <DevicesCountButton
                      devicesCount={devices?.length}
                      devices={devices}
                      project={project}
                    />
                  },
                  {
                    title: 'Project',
                    content: project?.title
                  }
                ]}
                diagram={
                  <StepProgressBarCnt>
                    <StepProgressBar
                      completedSteps={lastCompletedStepIndex + 1}
                      allSteps={delivery.length}
                      startDate={dateFormatter(startDate, account)}
                      endDate={
                        done ?
                          dateFormatter(done, account) : endDate ?
                            dateFormatter(endDate, account) : undefined
                      }
                      origin={originCountry}
                      destination={destinationCountry}
                      status={shipmentStatus.code}
                      StepbackgroundColor="white"
                    />
                  </StepProgressBarCnt>
                }
                onClickMore={() => {
                  setShipmentItem(shipment);
                  handleDrawerMenuOpen();
                }}
              />
            );
          })}
        </CardsCnt>
        <MobileDrawerMenu
          isOpen={isDrawerMenuOpen}
          onClose={setIsDrawerMenuOpen}
          menuTitle={shipmentItem?.label || formatter(shipmentItem?.uid)}
          menuItems={menuItems}
        />
        <DeleteDialog
          shipment={shipmentItem || {}}
          showDialog={showDeleteDialog}
          setShowDialog={setShowDeleteDialog}
          dispatch={dispatch}
          history={history}
          onDelete={handleDeleteShipmentSuccess}
        />
        {account.userType <= 20 &&
          <LargeFixedButton directTo="/addshipment" buttonName="Add shipment" />}
      </>
    ) : <PreloaderComponent />
  );
};

export default CardsView;
